import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  imageAlt?: string;
  /** og:type — 'website' (default), 'article', 'profile', etc. */
  type?: string;
  /** When true, asks crawlers not to index/follow (app, shared report, 404). */
  noindex?: boolean;
  /** Optional JSON-LD structured data object(s) injected into <head>. */
  jsonLd?: object | object[];
}

const SITE_URL = 'https://suitcasemuse.com';

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  path = '',
  image = '/logo.png',
  imageAlt,
  type = 'website',
  noindex = false,
  jsonLd,
}) => {
  const fullUrl = `${SITE_URL}${path}`;
  const fullTitle =
    title === 'Home'
      ? 'Suitcase Muse - AI-Powered Styling for Travel, Events & Personal'
      : `${title} | Suitcase Muse`;
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  const alt = imageAlt || fullTitle;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullUrl} />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1'}
      />

      {/* Open Graph */}
      <meta property="og:site_name" content="Suitcase Muse" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={alt} />
      <meta property="og:type" content={type} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={alt} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
